import React, { useEffect, useMemo, useState } from 'react';
import { Palette, Lock, AlertTriangle, Building2, Check, Upload } from 'lucide-react';
import { NAV_SECTIONS } from 'components/Layout/navConfig';
import { portalProfileService, type PortalProfile } from 'api/services/portalProfile.service';
import { organizationsService } from 'api/services/organizations.service';
import { mergeOrganizations, isProviderOrg, groupByProvider, type PickerOrg } from './organizationPicker';

/**
 * Author a reseller's portal: what it calls itself, and which tabs it offers.
 *
 * <h3>Why the tab picker is built from NAV_SECTIONS</h3>
 * The allowlist is a list of paths, and typing paths by hand does not work — the
 * obvious guess for Overview is "/dashboard", which this portal does not have
 * (it is "/"). A hand-typed path that matches nothing locks a tab for a reason
 * nobody can find afterwards. So the picker is generated from the real nav
 * tree — the one navConfig.ts holds and every breakpoint renders; every option
 * here is a path that actually exists.
 *
 * <h3>Brand is a provider's, the tab list can be a business's</h3>
 * A business inherits its provider's brand and cannot change it — that is the
 * guarantee white-labelling sells, and the server refuses brand fields on a
 * business. The tab list is genuinely per-customer, so it is editable for both.
 */

const PortalProfiles: React.FC = () => {
  const [orgs, setOrgs] = useState<PickerOrg[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [profile, setProfile] = useState<PortalProfile | null>(null);

  const [brandName, setBrandName] = useState('');
  const [brandTagline, setBrandTagline] = useState('');
  const [footerCompanyName, setFooterCompanyName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [allowed, setAllowed] = useState<string[]>([]);
  const [restrict, setRestrict] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const selected = useMemo(
    () => orgs.find((o) => o.id === selectedId) ?? null,
    [orgs, selectedId],
  );

  useEffect(() => {
    (async () => {
      // Providers and businesses in one list: a profile can be written for
      // either, and an administrator setting up a reseller needs to reach both
      // without knowing which endpoint holds which.
      const [providers, businesses] = await Promise.all([
        organizationsService.getServiceProviders(),
        organizationsService.getAllBusinesses(),
      ]);
      // /organizations/businesses returns PROVIDERS too — it filters on having a
      // businessId, not on type — so the two listings overlap and have to be
      // merged rather than concatenated. See organizationPicker.
      setOrgs(mergeOrganizations(
        providers.success && providers.data ? (providers.data as unknown as PickerOrg[]) : [],
        businesses.success && businesses.data ? (businesses.data as unknown as PickerOrg[]) : [],
      ));
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!selectedId) { setProfile(null); return; }
    setNote(null);
    (async () => {
      const res = await portalProfileService.forProvider(selectedId);
      if (res.success && res.data) {
        const p = res.data;
        setProfile(p);
        setBrandName(p.brandName ?? '');
        setBrandTagline(p.brandTagline ?? '');
        setFooterCompanyName(p.footerCompanyName ?? '');
        setLogoUrl(p.logoUrl ?? '');
        const paths = p.allowedPaths ?? [];
        setAllowed(paths);
        // An empty allowlist is "no restriction", so the toggle starts off. A
        // ticked box with an empty list would read as "everything is locked",
        // which is the opposite of what an empty list means.
        setRestrict(paths.length > 0);
      }
    })();
  }, [selectedId]);

  /**
   * Uploading stores the logo on the profile server-side and returns the whole
   * profile, so the preview below is what is actually saved rather than a local
   * object URL that would disappear on reload.
   */
  const uploadLogo = async (file: File) => {
    if (!selected) return;
    setUploading(true);
    setNote(null);
    const res = await portalProfileService.uploadLogo(selected.id, file);
    if (res.success && res.data) {
      setProfile(res.data);
      setLogoUrl(res.data.logoUrl ?? '');
      setNote({ kind: 'ok', text: 'Logo uploaded and saved.' });
    } else {
      setNote({ kind: 'err', text: res.error || 'Could not upload the logo.' });
    }
    setUploading(false);
  };

  const toggle = (path: string) =>
    setAllowed((prev) => (prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]));

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    setNote(null);

    const body: Record<string, unknown> = {
      // Sending an empty list is meaningful — it clears a restriction — so the
      // toggle, not the length, decides what is sent.
      allowedPaths: restrict ? allowed : [],
    };
    if (isProviderOrg(selected)) {
      body.brandName = brandName || null;
      body.brandTagline = brandTagline || null;
      body.footerCompanyName = footerCompanyName || null;
      // logoUrl is deliberately NOT sent here. The upload owns that field and
      // already persisted it; re-sending local state on Save would let a stale
      // value overwrite a logo uploaded moments earlier. The server only
      // touches keys it is given.
    }

    const res = await portalProfileService.save(selected.id, body);
    if (res.success && res.data) {
      setProfile(res.data);
      setNote({ kind: 'ok', text: `Saved. ${selected.name} now uses this portal.` });
    } else {
      setNote({ kind: 'err', text: res.error || 'Could not save.' });
    }
    setSaving(false);
  };

  if (loading) return <div className="p-6 text-gray-600 dark:text-gray-400">Loading…</div>;

  return (
    <div className="p-6 space-y-6 max-w-5xl">
      <header>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Portal profiles</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          What a reseller's portal calls itself, and which tabs its customers get. A business
          inherits its provider's brand and can only have its own tab list.
        </p>
      </header>

      <label className="block max-w-md">
        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Organization
        </span>
        <select
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-gray-100"
        >
          <option value="">Choose an organization…</option>
          {/* Grouped by provider. A platform admin can reach every organization,
              so a flat list says nothing about which reseller a business belongs
              to — and that relationship is the whole subject of this screen. */}
          {groupByProvider(orgs).map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.options.map((o) => (
                <option key={o.id} value={o.id}>
                  {isProviderOrg(o) ? `${o.name} — provider` : o.name}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>

      {selected && (
        <>
          {!isProviderOrg(selected) && (
            <div className="flex items-start gap-3 rounded-lg border border-blue-300 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20 p-4">
              <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <p className="text-sm text-blue-900 dark:text-blue-300">
                <strong>{selected.name}</strong> is a business, so its brand comes from its
                service provider and cannot be set here. Its tab list can.
              </p>
            </div>
          )}

          {isProviderOrg(selected) && (
            <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-5 space-y-4">
              <h2 className="flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
                <Palette className="w-4 h-4" /> Brand
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Every business under this provider sees these. Leave a field blank to keep
                whatever the portal already shows.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Name" value={brandName} onChange={setBrandName} placeholder="CatchMyCall" />
                <Field label="Tagline" value={brandTagline} onChange={setBrandTagline} />
                <Field label="Footer company" value={footerCompanyName} onChange={setFooterCompanyName} />
              </div>

              <div>
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Logo
                </span>
                <div className="flex items-center gap-4">
                  {logoUrl ? (
                    <img
                      src={logoUrl} alt="Current logo"
                      className="h-12 w-auto max-w-[12rem] object-contain rounded border border-gray-200 dark:border-gray-700 bg-white p-1"
                    />
                  ) : (
                    <span className="text-sm text-gray-500 dark:text-gray-400">No logo set</span>
                  )}
                  <label className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm text-gray-700 dark:text-gray-300">
                    <Upload className="w-4 h-4" />
                    {uploading ? 'Uploading…' : logoUrl ? 'Replace' : 'Upload'}
                    <input
                      type="file" accept="image/png,image/jpeg,image/webp"
                      className="hidden" disabled={uploading}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        // Clear the input so picking the SAME file again still
                        // fires a change event — otherwise a retry after a failed
                        // upload silently does nothing.
                        e.target.value = '';
                        if (f) uploadLogo(f);
                      }}
                    />
                  </label>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  PNG, JPEG or WEBP, under 2 MB. Saved immediately — the rest of this form
                  still needs Save.
                </p>
              </div>
              <p className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                The sign-in screen is not covered by this. Before someone logs in there is no
                organization to read a brand from, so login is branded by the domain.
              </p>
            </section>
          )}

          <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-5 space-y-4">
            <h2 className="flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
              <Lock className="w-4 h-4" /> Tabs
            </h2>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox" checked={restrict}
                onChange={(e) => setRestrict(e.target.checked)}
                className="mt-1 w-4 h-4"
              />
              <span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  Limit which tabs this portal offers
                </span>
                <span className="block text-sm text-gray-600 dark:text-gray-400">
                  Off means every tab is available. Tabs left unticked stay visible but locked,
                  so people can see where things will be.
                </span>
              </span>
            </label>

            {restrict && (
              <>
                {allowed.length === 0 && (
                  <p className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    Nothing is ticked. Saving now removes the limit rather than locking
                    everything — an empty list means "no restriction".
                  </p>
                )}
                <div className="space-y-4 max-h-[28rem] overflow-y-auto pr-2">
                  {NAV_SECTIONS.map((section) => (
                    <div key={section.id}>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                        {section.label}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                        {section.items.map((item) => (
                          <label key={item.path} className="flex items-center gap-2 py-1 cursor-pointer text-sm">
                            <input
                              type="checkbox"
                              checked={allowed.includes(item.path)}
                              onChange={() => toggle(item.path)}
                              className="w-4 h-4"
                            />
                            <span className="text-gray-900 dark:text-gray-100">{item.label}</span>
                            <code className="text-[11px] text-gray-500 dark:text-gray-500">{item.path}</code>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>

          <div className="flex items-center gap-3">
            <button
              onClick={save} disabled={saving}
              className="tl-btn tl-btn-primary disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            {profile?.allowedPathsSource && (
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Current tab list comes from the {profile.allowedPathsSource.toLowerCase()}.
              </span>
            )}
            {note && (
              <span className={`flex items-center gap-1 text-sm ${
                note.kind === 'ok' ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
              }`}>
                {note.kind === 'ok' && <Check className="w-4 h-4" />}{note.text}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const Field: React.FC<{
  label: string; value: string; onChange: (v: string) => void; placeholder?: string;
}> = ({ label, value, onChange, placeholder }) => (
  <label className="block">
    <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</span>
    <input
      value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-gray-100"
    />
  </label>
);

export default PortalProfiles;
